import { useGLTF } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { PerspectiveCamera, Vector3 } from 'three';
import { detectarMallas, HUESOS, requerirHueso } from './mallas';
import { aplicarPoseDeReposo, buscarHuesosCuerpo } from './poseReposo';
import { ThreeRigAdapter } from './ThreeRigAdapter';

/**
 * Canvas del avatar: carga el `.glb` indicado, monta el `ThreeRigAdapter`
 * y encuadra en busto con iluminación neutra tipo videollamada
 * (`04_ARQUITECTURA.md` §6.6).
 *
 * No elige el asset: la URL la resuelve el manifiesto (`manifiesto.ts`).
 * Si el `.glb` no carga o no cumple el estándar, el error sube al límite
 * de errores de `AvatarStage`, que muestra el marcador (D-9).
 */

/**
 * Altura visible del encuadre, en metros: cabeza, hombros y algo de
 * pecho. Hasta la tanda 4 eran 0,42 m centrados más arriba, que dejaba
 * aire sobre la cabeza y cortaba los hombros justo en el borde. Con los
 * brazos ya abajo (D-1), bajar y ampliar el cuadro da el plano de
 * videollamada; con los brazos en T no se podía, porque entraban.
 */
const FRAME_HEIGHT_M = 0.52;
/**
 * Cuánto está el centro del encuadre por encima del hueso `Head`. El
 * hueso está en la base del cráneo: apuntar exactamente ahí deja la
 * frente al borde.
 */
const FACE_CENTER_OFFSET_M = 0.03;
/**
 * Planos de recorte, como fracción de la distancia cámara–cara. La
 * precisión del depth buffer depende casi toda del plano cercano: con
 * `near` fijo en 0,05 m y la cara a ~1 m, en Firefox el párpado cerrado
 * perdía contra la córnea, que en algunos avatares queda a décimas de
 * milímetro detrás (2026-10-14), y la pupila asomaba sobre el párpado.
 * A media distancia no se recorta nada del busto y la precisión en la
 * cara mejora unas diez veces.
 */
const NEAR_FRACTION = 0.5;
const FAR_FRACTION = 4;

interface AvatarModelProps {
  url: string;
  onAdapterReady: (adapter: ThreeRigAdapter) => void;
  onFrame?: ((deltaMs: number) => void) | undefined;
}

function AvatarModel({ url, onAdapterReady, onFrame }: AvatarModelProps) {
  const { scene } = useGLTF(url);
  const camera = useThree((s) => s.camera);
  const adapterRef = useRef<ThreeRigAdapter | null>(null);

  // `useGLTF` cachea la escena entre montajes. En StrictMode el efecto
  // corre dos veces sobre el mismo objeto: construir el adapter dos
  // veces es idempotente (sólo lee el diccionario y escribe la pose).
  //
  // Las mallas se detectan por sus blend shapes, no por nombre
  // (`mallas.ts`); los huesos sí se buscan por nombre (06 §7, check 3).
  const adapter = useMemo(() => {
    // Pose de reposo (tanda 4, D-1): los assets vienen en T-pose y en el
    // encuadre de busto los brazos cruzan el borde inferior. Se aplica una
    // sola vez, antes de construir el adapter, y es idempotente.
    const cuerpo = buscarHuesosCuerpo(scene);
    const faltan = aplicarPoseDeReposo(scene, cuerpo);
    if (faltan.length > 0) {
      console.warn(
        `[avatar] sin huesos de ${faltan.join(' ni ')} en ${url}: se muestra la pose del asset. Ver docs/06_ESTANDAR_RIG.md §3.`,
      );
    }
    return new ThreeRigAdapter(detectarMallas(scene), {
      head: requerirHueso(scene, HUESOS.cabeza),
      eyeLeft: requerirHueso(scene, HUESOS.ojoIzq),
      eyeRight: requerirHueso(scene, HUESOS.ojoDer),
      chest: cuerpo.pecho,
    });
  }, [scene, url]);

  useEffect(() => {
    adapterRef.current = adapter;
    onAdapterReady(adapter);
  }, [adapter, onAdapterReady]);

  // Encuadre de busto: la cámara se ubica respecto de la posición real
  // del hueso de la cabeza, no con números mágicos — así el mismo código
  // sirve cuando entre el avatar propio (T1) con otra escala.
  useLayoutEffect(() => {
    // R3F tipa `camera` como perspectiva | ortográfica. El encuadre se
    // calcula con el `fov`, que sólo existe en la primera.
    if (!(camera instanceof PerspectiveCamera)) return;

    scene.updateMatrixWorld(true);
    const headWorld = new Vector3();
    requerirHueso(scene, HUESOS.cabeza).getWorldPosition(headWorld);

    // El hueso `Head` está en la base del cráneo, no en el centro de la
    // cara: apuntar directo a él deja la frente fuera de cuadro. El
    // centro visual de la cara está unos centímetros más arriba.
    const target = new Vector3(headWorld.x, headWorld.y + FACE_CENTER_OFFSET_M, headWorld.z);

    // `fov` es vertical, así que la altura visible sólo depende de la
    // distancia: altura = 2·d·tan(fov/2). Despejamos d para encuadrar
    // `FRAME_HEIGHT_M` (cabeza + hombros) sin importar el aspect ratio.
    const fovRad = (camera.fov * Math.PI) / 180;
    const distance = FRAME_HEIGHT_M / (2 * Math.tan(fovRad / 2));

    camera.position.set(target.x, target.y, target.z + distance);
    camera.lookAt(target);
    camera.near = distance * NEAR_FRACTION;
    camera.far = distance * FAR_FRACTION;
    camera.updateProjectionMatrix();
  }, [scene, camera]);

  useFrame((_state, delta) => {
    const deltaMs = delta * 1000;
    adapterRef.current?.update(deltaMs);
    onFrame?.(deltaMs);
  });

  return <primitive object={scene} />;
}

/**
 * Techo de cuadros por segundo del limitador, **cuando se activa**.
 *
 * No se usa por defecto: ver `AvatarCanvasProps.fpsMax`.
 */
const FPS_MAX = 60;

/**
 * Limita la tasa de render. Requiere `frameloop="demand"`: en modo
 * `always` R3F dibuja en cada `requestAnimationFrame`, y saltear trabajo
 * dentro de `useFrame` no evitaría el costo real, que es el de la GPU.
 * En modo demanda sólo se dibuja cuando alguien invalida, y quien
 * invalida es este loop, a lo sumo `FPS_MAX` veces por segundo.
 *
 * Se probó también `frameloop="never"` + `advance()`, que en teoría da
 * una cadencia exacta por ser el único rAF en juego. **No sirve acá:**
 * `advance()` dibuja pero no ejecuta los callbacks de `useFrame`, así
 * que el adapter deja de tickear, la capa idle se congela y el avatar
 * queda estático. Verificado en el navegador — 0 frames contados.
 *
 * **Sólo puede cuantizar a divisores enteros del refresco**, porque lo
 * único que hace es dejar pasar o no cada `requestAnimationFrame`. En
 * escritorio a 144 Hz da 48 (uno de cada tres); en un Android de 60 Hz
 * dio 30 (uno de cada dos) donde debería haber dado 60 — el margen de
 * 1 ms no sobrevive al jitter del rAF. Por eso no se usa por defecto:
 * ver `AvatarCanvasProps.fpsMax`.
 */
function LimitadorDeFps({ max = FPS_MAX }: { max?: number }) {
  const invalidate = useThree((s) => s.invalidate);

  useEffect(() => {
    const minMs = 1000 / max;
    let raf = 0;
    let ultimo = 0;
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      // Margen de 1 ms: sin él, un frame que llega a 16,6 en vez de 16,7
      // se saltea y la tasa efectiva cae a la mitad.
      if (t - ultimo >= minMs - 1) {
        ultimo = t;
        invalidate();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [invalidate, max]);

  return null;
}

/**
 * Devuelve el contenido del canvas como `data:image/png`, o `null` si no
 * se pudo leer. Ver `PuenteDeCaptura`.
 */
export type Capturar = () => string | null;

/**
 * Expone hacia afuera una función para capturar el canvas (tanda 5).
 * Sólo lo usa `/dev/avatar`; el producto no pasa `onCapturador`.
 *
 * **El render se fuerza en el mismo turno que la lectura.** El contexto
 * WebGL se crea sin `preserveDrawingBuffer`, así que el buffer de dibujo
 * queda indefinido apenas el navegador compone el frame: leerlo más tarde
 * devuelve una imagen negra o vacía. Activar esa opción arreglaría la
 * lectura, pero cuesta rendimiento en todos los frames y el `<Canvas>` es
 * el mismo que monta la entrevista — se pagaría en producto para una
 * herramienta de desarrollo.
 */
function PuenteDeCaptura({ onCapturador }: { onCapturador: (c: Capturar | null) => void }) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);

  useEffect(() => {
    const capturar: Capturar = () => {
      gl.render(scene, camera);
      return gl.domElement.toDataURL('image/png');
    };
    onCapturador(capturar);
    return () => {
      onCapturador(null);
    };
  }, [gl, scene, camera, onCapturador]);

  return null;
}

export interface AvatarCanvasProps {
  /** URL del `.glb`, ya resuelta por el manifiesto. */
  url: string;
  onAdapterReady: (adapter: ThreeRigAdapter) => void;
  /**
   * Techo de cuadros por segundo. **Default `null`: sin límite.**
   *
   * El limitador existía para no pedirle 120 fps a un teléfono, pero
   * medido en un Adreno 810 resultó ser una pesimización: con cap daba
   * 30 FPS y 1% low 26; sin cap, 60 FPS y 1% low 48. Tiraba uno de cada
   * dos cuadros.
   *
   * La causa es que la compuerta tiene 1 ms de margen sobre un período
   * de 16,67, así que en una pantalla de 60 Hz cualquier jitter del
   * `requestAnimationFrame` la hace fallar y el frame se pierde hasta el
   * siguiente vsync. Sumado a que conviven dos rAF cuyo orden no está
   * garantizado, sobre 60 Hz el limitador sólo puede empatar o empeorar.
   *
   * Se conserva porque distingue "GPU saturada" de "limitador tirando
   * cuadros" —los dos hunden la tasa a un divisor entero del refresco— y
   * esa ambigüedad ya costó un diagnóstico equivocado. La página de
   * medición lo conmuta.
   */
  fpsMax?: number | null;
  /**
   * Se llama una vez por frame renderizado, con el delta en ms. Existe
   * para la medición de rendimiento de F0.6 paso 6; el producto no la
   * usa. Sólo corre una vez que el `.glb` cargó — antes de eso no hay
   * render loop que medir.
   */
  onFrame?: (deltaMs: number) => void;
  /**
   * Recibe la función de captura del canvas, y `null` al desmontarse.
   * Existe para las descargas por expresión de `/dev/avatar`; el producto
   * no la usa.
   */
  onCapturador?: (capturar: Capturar | null) => void;
}

export function AvatarCanvas({
  url,
  onAdapterReady,
  onFrame,
  onCapturador,
  fpsMax = null,
}: AvatarCanvasProps) {
  return (
    <Canvas
      // Cap del devicePixelRatio: en móvil de gama media renderizar a 3x
      // es el primer costo a recortar (F0.6 paso 6).
      dpr={[1, 2]}
      // Con cap, quien decide cuándo se dibuja es `LimitadorDeFps`. Sin
      // cap, R3F vuelve a dibujar en cada frame del navegador.
      frameloop={fpsMax === null ? 'always' : 'demand'}
      // `near` y `far` los fija el encuadre según la distancia a la cara.
      camera={{ fov: 28 }}
      gl={{ antialias: true }}
    >
      {fpsMax !== null && <LimitadorDeFps max={fpsMax} />}
      {onCapturador && <PuenteDeCaptura onCapturador={onCapturador} />}
      {/* Luz neutra de videollamada: key frontal suave, fill lateral
          para que la nariz no proyecte sombra dura, y ambiente bajo
          para que las cuencas no queden negras. Sin HDR/Environment:
          traería un fetch a CDN externo. */}
      <ambientLight intensity={0.55} />
      <directionalLight position={[0.4, 0.8, 2]} intensity={1.8} />
      <directionalLight position={[-1.2, 0.3, 0.8]} intensity={0.5} />
      <Suspense fallback={null}>
        <AvatarModel url={url} onAdapterReady={onAdapterReady} onFrame={onFrame} />
      </Suspense>
    </Canvas>
  );
}
